import { useNavigate, useLocation, type NavigateOptions } from 'react-router';
import { ROUTE_PATHS, generatePath } from './routePaths';
import type { ItemPrefillData } from './types';
import { usePrefillStore } from '@/stores';

interface UseRouterNavigationReturn {
  // Базовая навигация
  navigate: (path: string, options?: NavigateOptions) => void;
  back: () => void;
  
  // Home routes (обычные переходы)
  toHomeFeed: () => void;
  toHomeSelections: () => void;
  toHomePopular: () => void;
  toHomeWishlists: () => void;
  
  // Home routes (переключение табов без добавления в history)
  switchToHomeFeed: () => void;
  switchToHomeSelections: () => void;
  switchToHomePopular: () => void;
  switchToHomeWishlists: () => void;
  
  // Bottom Menu routes (переключение без добавления в history)
  switchToCommunity: () => void;
  switchToMyWishlists: () => void;
  switchToProfile: () => void;
  
  // Wishlist routes
  toMyWishlists: () => void;
  toWishlistDetail: (wishlistId: string) => void;
  toWishlistNew: () => void;
  toWishlistEdit: (wishlistId: string) => void;
  
  // Item routes
  toItemDetail: (wishlistId: string, itemId: string) => void;
  toItemNew: (wishlistId: string) => void;
  toItemEdit: (wishlistId: string, itemId: string) => void;
  /** Навигация на форму создания желания с предзаполненными данными (копирование чужого желания) */
  toItemNewPrefilled: (prefillData: ItemPrefillData) => void;
  
  // Community routes
  toCommunity: () => void;
  toUserProfile: (userId: string) => void;
  toUserWishlists: (userId: string) => void;
  toUserItems: (userId: string) => void;
  
  // Profile routes
  toProfile: () => void;
  toEditProfile: () => void;
  toMyItems: () => void;
  toSettings: () => void;
  toNotifications: () => void;
  
  // Bookings routes
  toBookingsList: () => void;
  
  // Secret Santa routes
  toSecretSantaList: () => void;
  toSecretSantaNew: () => void;
  toSecretSantaDetail: (gameId: string) => void;
  toSecretSantaEdit: (gameId: string) => void;

  // Selection routes
  toSelectionDetail: (selectionId: string) => void;
  toSelectionItemDetail: (selectionId: string, itemId: string) => void;

  // Auth routes
  toWelcome: () => void;
  toSignIn: () => void;
  toSignUp: () => void;
  toForgotPassword: () => void;
  toResetPassword: () => void;
}

/**
 * Хук для навигации между страницами приложения
 */
export function useRouterNavigation(): UseRouterNavigationReturn {
  const navigate = useNavigate();
  const location = useLocation();

  const back = () => {
    // Используем нативную browser history - она всегда знает откуда пришёл пользователь
    navigate(-1);
  };
  
  return {
    // Базовая навигация
    navigate: (path: string, options?: NavigateOptions) => navigate(path, options),
    back: back,
    
    // Home routes (обычные переходы)
    toHomeFeed: () => navigate(ROUTE_PATHS.HOME_FEED),
    toHomeSelections: () => navigate(ROUTE_PATHS.HOME_SELECTIONS),
    toHomePopular: () => navigate(ROUTE_PATHS.HOME_POPULAR),
    toHomeWishlists: () => navigate(ROUTE_PATHS.HOME_WISHLISTS),
    
    // Home routes (переключение табов без добавления в history)
    switchToHomeFeed: () => navigate(ROUTE_PATHS.HOME_FEED, { replace: true }),
    switchToHomeSelections: () => navigate(ROUTE_PATHS.HOME_SELECTIONS, { replace: true }),
    switchToHomePopular: () => navigate(ROUTE_PATHS.HOME_POPULAR, { replace: true }),
    switchToHomeWishlists: () => navigate(ROUTE_PATHS.HOME_WISHLISTS, { replace: true }),
    
    // Bottom Menu routes (переключение без добавления в history)
    switchToCommunity: () => navigate(ROUTE_PATHS.COMMUNITY, { replace: true }),
    switchToMyWishlists: () => navigate(ROUTE_PATHS.MY_WISHLISTS, { replace: true }),
    switchToProfile: () => navigate(ROUTE_PATHS.PROFILE, { replace: true }),
    
    // Wishlist routes
    toMyWishlists: () => navigate(ROUTE_PATHS.MY_WISHLISTS),
    toWishlistDetail: (wishlistId: string) => navigate(generatePath.wishlistDetail(wishlistId)),
    toWishlistNew: () => navigate(ROUTE_PATHS.WISHLIST_NEW),
    toWishlistEdit: (wishlistId: string) => navigate(generatePath.wishlistEdit(wishlistId)),
    
    // Item routes
    toItemDetail: (wishlistId: string, itemId: string) => navigate(generatePath.itemDetail(wishlistId, itemId)),
    toItemNew: (wishlistId: string) => navigate(generatePath.itemNew(wishlistId)),
    toItemEdit: (wishlistId: string, itemId: string) => navigate(generatePath.itemEdit(wishlistId, itemId)),
    /** Навигация на форму создания желания с предзаполненными данными (копирование чужого желания) */
    toItemNewPrefilled: (prefillData: ItemPrefillData) => {
      usePrefillStore.getState().setPrefillData(prefillData);
      navigate(ROUTE_PATHS.ITEM_NEW_PREFILLED);
    },
    
    // Community routes
    toCommunity: () => navigate(ROUTE_PATHS.COMMUNITY),
    toUserProfile: (userId: string) => navigate(generatePath.userProfile(userId)),
    toUserWishlists: (userId: string) => navigate(generatePath.userWishlists(userId)),
    toUserItems: (userId: string) => navigate(generatePath.userItems(userId)),
    
    // Profile routes
    toProfile: () => navigate(ROUTE_PATHS.PROFILE),
    toEditProfile: () => navigate(ROUTE_PATHS.PROFILE_EDIT),
    toMyItems: () => navigate(ROUTE_PATHS.MY_ITEMS),
    toSettings: () => navigate(ROUTE_PATHS.SETTINGS),
    toNotifications: () => navigate(ROUTE_PATHS.NOTIFICATIONS),
    
    // Bookings routes
    toBookingsList: () => navigate(ROUTE_PATHS.BOOKINGS_LIST),
    
    // Secret Santa routes
    toSecretSantaList: () => navigate(ROUTE_PATHS.SECRET_SANTA_LIST),
    toSecretSantaNew: () => navigate(ROUTE_PATHS.SECRET_SANTA_NEW, { state: { origin: location.pathname } }),
    toSecretSantaDetail: (gameId: string) => navigate(generatePath.secretSantaDetail(gameId)),
    toSecretSantaEdit: (gameId: string) => navigate(generatePath.secretSantaEdit(gameId)),

    // Selection routes
    toSelectionDetail: (selectionId: string) => navigate(generatePath.selectionDetail(selectionId)),
    toSelectionItemDetail: (selectionId: string, itemId: string) => navigate(generatePath.selectionItemDetail(selectionId, itemId)),

    // Auth routes
    toWelcome: () => navigate(ROUTE_PATHS.WELCOME),
    toSignIn: () => navigate(ROUTE_PATHS.SIGN_IN),
    toSignUp: () => navigate(ROUTE_PATHS.SIGN_UP),
    toForgotPassword: () => navigate(ROUTE_PATHS.FORGOT_PASSWORD),
    toResetPassword: () => navigate(ROUTE_PATHS.RESET_PASSWORD),
  };
}